import React, { useState, useEffect } from 'react';
import AppLayout from '../../components/AppLayout';
import ProspectingNav from '../../components/prospecting/ProspectingNav';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { Search, Users, Target, TrendingUp, Upload, ArrowRight, Plus, Filter, Radar, Shield, MessageSquare, FileText, BarChart3 } from 'lucide-react';
import { STAGE_MAP } from '../../components/prospecting/constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProspectingDashboard() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/prospecting/dashboard`, { credentials: 'include' });
      if (res.ok) {
        setDashboard(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ label, value, icon: Icon, color, onClick }) => (
    <button
      onClick={onClick}
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
      className={`${isDark ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-white border-gray-200 hover:bg-gray-50'} border rounded-xl p-5 text-left transition-all`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{value}</p>
      <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
    </button>
  );

  if (loading) {
    return (
      <AppLayout title="Lead Finder">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#A2FF00]"></div>
        </div>
      </AppLayout>
    );
  }

  const d = dashboard || { total_leads: 0, qualified: 0, contacted: 0, converted: 0, pipeline_stages: {}, icp_count: 0, qualification_rate: 0, conversion_rate: 0, recent_leads: [] };

  return (
    <AppLayout title="Lead Finder">
      <div className="space-y-6" data-testid="prospecting-dashboard">
        <ProspectingNav />
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Lead Finder</h1>
            <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Discover, qualify, and convert LinkedIn prospects</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/lead-finder/import')}
              data-testid="import-leads-btn"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} transition-colors`}
            >
              <Upload className="w-4 h-4" />
              Import Leads
            </button>
            <button
              onClick={() => navigate('/lead-finder/pipeline')}
              data-testid="view-pipeline-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#A2FF00] text-black font-medium hover:bg-[#8FE600] transition-colors"
            >
              <Plus className="w-4 h-4" />
              View Pipeline
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Leads" value={d.total_leads} icon={Users} color="bg-blue-600" onClick={() => navigate('/lead-finder/pipeline')} />
          <StatCard label="Qualified" value={d.qualified} icon={Target} color="bg-green-600" onClick={() => navigate('/lead-finder/pipeline?stage=qualified')} />
          <StatCard label="Contacted" value={d.contacted} icon={Search} color="bg-purple-600" onClick={() => navigate('/lead-finder/pipeline?stage=contacted')} />
          <StatCard label="Converted" value={d.converted} icon={TrendingUp} color="bg-emerald-600" onClick={() => navigate('/lead-finder/pipeline?stage=converted')} />
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pipeline Overview */}
          <div className={`lg:col-span-2 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'} border rounded-xl p-5`}>
            <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Pipeline Overview</h2>
            <div className="space-y-3">
              {Object.entries(STAGE_MAP).map(([key, config]) => {
                const count = d.pipeline_stages?.[key] || 0;
                const pct = d.total_leads > 0 ? (count / d.total_leads * 100) : 0;
                return (
                  <div key={key} className="flex items-center gap-3">
                    <span className={`w-28 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{config.label}</span>
                    <div className={`flex-1 h-6 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
                      <div
                        className={`h-full rounded-full ${config.color} transition-all duration-500`}
                        style={{ width: `${Math.max(pct, 0)}%` }}
                      />
                    </div>
                    <span className={`w-10 text-right text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick Actions & Metrics */}
          <div className="space-y-4">
            <div className={`${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'} border rounded-xl p-5`}>
              <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Quick Actions</h2>
              <div className="space-y-2">
                <button
                  onClick={() => navigate('/lead-finder/icp')}
                  data-testid="manage-icp-btn"
                  className={`w-full flex items-center justify-between p-3 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-50'} transition-colors`}
                >
                  <div className="flex items-center gap-3">
                    <Target className={`w-5 h-5 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
                    <span className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Manage ICPs ({d.icp_count})</span>
                  </div>
                  <ArrowRight className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                </button>
                <button
                  onClick={() => navigate('/lead-finder/import')}
                  data-testid="quick-import-btn"
                  className={`w-full flex items-center justify-between p-3 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-50'} transition-colors`}
                >
                  <div className="flex items-center gap-3">
                    <Upload className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                    <span className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Import Leads</span>
                  </div>
                  <ArrowRight className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                </button>
                <button
                  onClick={() => navigate('/lead-finder/scraping')}
                  data-testid="quick-scraping-btn"
                  className={`w-full flex items-center justify-between p-3 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-50'} transition-colors`}
                >
                  <div className="flex items-center gap-3">
                    <Radar className={`w-5 h-5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                    <span className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Scraping Jobs</span>
                  </div>
                  <ArrowRight className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                </button>
                <button
                  onClick={() => navigate('/lead-finder/sequences')}
                  data-testid="quick-sequences-btn"
                  className={`w-full flex items-center justify-between p-3 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-50'} transition-colors`}
                >
                  <div className="flex items-center gap-3">
                    <MessageSquare className={`w-5 h-5 ${isDark ? 'text-teal-400' : 'text-teal-600'}`} />
                    <span className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Sequences</span>
                  </div>
                  <ArrowRight className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                </button>
                <button
                  onClick={() => navigate('/lead-finder/templates')}
                  data-testid="quick-templates-btn"
                  className={`w-full flex items-center justify-between p-3 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-50'} transition-colors`}
                >
                  <div className="flex items-center gap-3">
                    <FileText className={`w-5 h-5 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
                    <span className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Message Templates</span>
                  </div>
                  <ArrowRight className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                </button>
                <button
                  onClick={() => navigate('/lead-finder/admin')}
                  data-testid="quick-admin-btn"
                  className={`w-full flex items-center justify-between p-3 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-50'} transition-colors`}
                >
                  <div className="flex items-center gap-3">
                    <Filter className={`w-5 h-5 ${isDark ? 'text-orange-400' : 'text-orange-600'}`} />
                    <span className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Admin Settings</span>
                  </div>
                  <ArrowRight className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                </button>
                <button
                  onClick={() => navigate('/lead-finder/analytics')}
                  data-testid="quick-analytics-btn"
                  className={`w-full flex items-center justify-between p-3 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-50'} transition-colors`}
                >
                  <div className="flex items-center gap-3">
                    <BarChart3 className={`w-5 h-5 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
                    <span className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Analytics</span>
                  </div>
                  <ArrowRight className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                </button>
              </div>
            </div>

            <div className={`${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'} border rounded-xl p-5`}>
              <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Conversion Metrics</h2>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-1">
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Qualification Rate</span>
                    <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{d.qualification_rate}%</span>
                  </div>
                  <div className={`h-2 rounded-full ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
                    <div className="h-full rounded-full bg-green-500" style={{ width: `${d.qualification_rate}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Conversion Rate</span>
                    <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{d.conversion_rate}%</span>
                  </div>
                  <div className={`h-2 rounded-full ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
                    <div className="h-full rounded-full bg-emerald-500" style={{ width: `${d.conversion_rate}%` }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Leads */}
        <div className={`${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'} border rounded-xl p-5`}>
          <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Recent Leads</h2>
          {d.recent_leads && d.recent_leads.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                    <th className={`text-left py-2 px-3 text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Name</th>
                    <th className={`text-left py-2 px-3 text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Company</th>
                    <th className={`text-left py-2 px-3 text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Stage</th>
                    <th className={`text-left py-2 px-3 text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Score</th>
                  </tr>
                </thead>
                <tbody>
                  {d.recent_leads.map((lead, i) => {
                    const stageConf = STAGE_MAP[lead.stage] || { label: lead.stage, color: 'bg-gray-500' };
                    return (
                      <tr key={lead.lead_id || i} className={`border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                        <td className={`py-3 px-3 text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                          {lead.linkedin?.first_name || ''} {lead.linkedin?.last_name || ''}
                        </td>
                        <td className={`py-3 px-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {lead.linkedin?.current_company || '-'}
                        </td>
                        <td className="py-3 px-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium text-white ${stageConf.color}`}>
                            {stageConf.label}
                          </span>
                        </td>
                        <td className={`py-3 px-3 text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                          {lead.scoring?.score ?? '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={`text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No leads yet. Import leads or start a scraping job to get started.</p>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
